import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import BasePage from '../../../BasePage/BasePage';
import ImageCarousel from '../../../../atoms/ImageCarousel/ImageCarousel';
import Grid from '../../../../atoms/Grid/Grid';
import InputControl from '../../../../atoms/InputControl/InputControl';
import TagsInput from '../../../../atoms/TagsInput/TagsInput';
import DynastySelector from '../../../../atoms/DynastySelector/DynastySelector';
import CitySelector from '../../../../atoms/CitySelector/CItySelector';
import Button from '../../../../atoms/Button/Button';
import Icon from '../../../../atoms/Icon/Icon';
import {
  fetchComponentsInitAction,
  updateSelectedComponentAction,
} from '../../../../../actions/instances/components';
import BuildingsSelector from '../../../../atoms/BuildingsSelector/BuildingsSelector';
import Api from '../../../../../services/Api';
import ComponentTypeSelector from '../../../../atoms/ComponentTypeSelector/ComponentTypeSelector';
import extractErrors from '../../../../../helpers/errors';
import InstanceDocumentEditor from '../../../../Organisms/InstanceDocumentEditor/InstanceDocumentEditor';
import CitationEditor from '../../../../molecules/CitationEditor/CitationEditor';
import CharacteristicsSelector from '../../../../atoms/CharacteristicsSelector/CharacteristicsSelector';
import './CreateComponent.scss';

const CreateComponent = () => {
  const { singleComponent } = useSelector(state => state.buildingComponentInstance);
  const [component, setComponent] = useState(singleComponent);
  useEffect(() => {
    if (singleComponent) {
      Api.get(`instances/building-components/${singleComponent.id}`).then(response => setComponent(response.data));
    }
  }, []);
  let mainImage;
  let restOfImages = [];
  if (component && component.images) {
    if (component.images.length > 0) {
      [mainImage] = component.images;
      restOfImages = component.images.slice(0, 1);
    }
  }
  const [imagesIds, setImagesIds] = useState(restOfImages);
  const [mainImageId, setMainImageId] = useState(mainImage);
  const [name, setName] = useState(component ? component.name : undefined);
  const [typeId, setTypeId] = useState(component ? component.component_type_id : undefined);
  const [dynastyId, setDynastyId] = useState(component ? component.dynasty_id : undefined);
  const [dynastyCode, setDynastyCode] = useState(component ? component.dynasty.code : undefined);
  const [cityId, setCityId] = useState(component ? component.city.id : undefined);
  const [cityCode, setCityCode] = useState(component ? component.city.code : undefined);
  const [resourcesIds, setResourcesIds] = useState(undefined);
  const [errors, setErrors] = useState(undefined);
  const [tagsIds, setTagsIds] = useState(
    component && component.tags ? component.tags.map(t => t.id) : undefined,
  );
  const [buildingsIds, setBuildingsIds] = useState(
    component && component.component_buildings
      ? component.component_buildings.map(x => x.id) : undefined,
  );
  const [characteristicsIds, setCharacteristicsIds] = useState(
    component && component.characteristics
      ? component.characteristics.map(x => x.id) : undefined,
  );
  const [documentsIds, setDocumentsIds] = useState(
    component ? component.documents.map(doc => doc.id) : [],
  );
  const [loading, setLoading] = useState(false);
  const [identifier, setIdentifier] = useState(undefined);
  useEffect(() => {
    let combinedCode = '';
    if (dynastyCode) {
      combinedCode += `${dynastyCode}-`;
    }
    if (cityCode) {
      combinedCode += `${cityCode}-`;
    }
    combinedCode += 'BCNXXXX';
    setIdentifier(combinedCode);
  }, [cityCode, dynastyCode]);
  useEffect(() => {
    if (component && component.component_buildings) {
      setBuildingsIds(component.component_buildings.map(x => x.id));
    }
    if (component && component.characteristics) {
      setCharacteristicsIds(component.characteristics.map(x => x.id));
    }
  }, [component]);
  const dispatch = useDispatch();
  const history = useHistory();
  const handleSave = () => {
    if (!name) {
      setErrors('Component name is required');
      return;
    }
    if (!cityId) {
      setErrors('You must select a region for the component');
      return;
    }
    if (!dynastyId) {
      setErrors('You must select a dynasty for the component');
      return;
    }
    setLoading(true);
    const data = {
      name,
      code_suffix: 'BCN',
      city_id: cityId,
      dynasty_id: dynastyId,
      images_ids: [mainImageId, ...imagesIds].filter(img => img !== undefined),
      component_type_id: typeId,
      tags_ids: tagsIds,
      buildings_ids: buildingsIds,
      resources_ids: resourcesIds,
      documents_ids: documentsIds,
      characteristics_ids: characteristicsIds,
    };
    const callback = (response) => {
      if (response.status === 200 || response.status === 201) {
        dispatch(fetchComponentsInitAction());
        dispatch(updateSelectedComponentAction(response.data));
        setTimeout(() => {
          setLoading(false);
          history.goBack();
        }, 500);
      } else {
        const errs = extractErrors(response.data);
        setErrors(errs[0]);
        setLoading(false);
      }
    };
    if (component) {
      Api.patch(`instances/building-components/${component.id}`, data).then(response => callback(response));
    } else {
      Api.post('instances/building-components', data).then(response => callback(response));
    }
  };
  return (
    <BasePage>
      <div className="create-component-container">
        <h2 className="col-md-12">Create new component</h2>
        <br />
        <Grid>
          <div className="col-md-3 left-column">
            <ImageCarousel
              initialImages={component ? component.images : []}
              additionalImagesAddedCallback={images => setImagesIds(images)}
              mainImageAddedCallback={imageId => setMainImageId(imageId)}
            />
            <hr />
            <div className="col-md-12 dynasty-section">
              <InstanceDocumentEditor
                initialDocuments={component ? component.documents : []}
                onFilesChanged={(files) => {
                  setDocumentsIds(files.map(f => f.id));
                }}
              />
            </div>
            <div className="tags-container">
              <label className="tag-label">Component Tags</label>
              <TagsInput tags={tagsIds} placeholder="Start typing..." onChange={tags => setTagsIds(tags)} />
            </div>
          </div>
          <div className="col-md-9 content-column">
            <Grid>
              <div className="col-md-12">
                <label>Type</label>
                <ComponentTypeSelector
                  selectedId={typeId}
                  isMulti={false}
                  onChanged={e => setTypeId(e.value)}
                />
              </div>
              <div className="col-md-12 dynasty-section">
                <label>Location</label>
                <CitySelector
                  onChanged={(city) => {
                    setCityId(city.value);
                    setCityCode(city.code);
                  }}
                  selectedId={cityId}
                />
              </div>
              <div className="col-md-12 dynasty-section">
                <label>Component Dynasty</label>
                <DynastySelector
                  onChanged={(dynasty) => {
                    setDynastyId(dynasty.value);
                    setDynastyCode(dynasty.code);
                  }}
                  selectedId={dynastyId}
                />
              </div>
              <div className="col-md-12">
                <InputControl label="Component name" value={name} type="text" onValueChanged={value => setName(value)} placeholder="Name..." />
                <br />
                {identifier}
              </div>
              <div className="col-md-12 dynasty-section">
                <label>Component Buildings</label>
                <BuildingsSelector
                  cityId={cityId}
                  dynastyId={dynastyId}
                  onChanged={(values) => {
                    const ids = values.map(val => parseInt(val.value, 10));
                    setBuildingsIds(ids);
                  }}
                  selectedIds={buildingsIds}
                />
              </div>
              <div className="col-md-12 dynasty-section">
                <label>Component Characteristics</label>
                <CharacteristicsSelector
                  onChanged={(values) => {
                    const ids = values.map(val => parseInt(val.value, 10));
                    setCharacteristicsIds(ids);
                  }}
                  selectedIds={characteristicsIds}
                  isMulti
                />
              </div>
              <div className="col-md-12 dynasty-section">
                <CitationEditor
                  onChange={values => setResourcesIds(values)}
                  initialCitations={component ? component.citation : []}
                />
              </div>
              {errors && (
                <div className="col-md-12 errors-container">
                  <span className="error">{errors}</span>
                </div>
              )}
              <div className="col-md-12 actions">
                <Button onInvoked={() => history.goBack()}>
                  <Icon icon="times" />
                  <span>Cancel</span>
                </Button>
                <Button onInvoked={handleSave} isLoading={loading}>
                  <Icon icon="save" />
                  <span>Save</span>
                </Button>
              </div>
            </Grid>
          </div>
        </Grid>
      </div>
    </BasePage>
  );
};

export default CreateComponent;
